import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, Send, Plus, Loader2 } from 'lucide-react';

/**
 * ChatPanel — Realtime teacher↔admin chat using FastAPI WebSockets.
 */
export const ChatPanel = ({ currentUser }) => {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [composer, setComposer] = useState('');
  const [loading, setLoading] = useState(true);
  const [wsState, setWsState] = useState('connecting');
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const activeIdRef = useRef(null);
  const refetchSignal = useRef(0);
  const [refetchVer, setRefetchVer] = useState(0);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  // Load threads (extracted to keep lint rule happy)
  const loadThreads = async (autoCreate) => {
    try {
      const res = await api.get('/chat/threads');
      const list = res.data || [];
      setThreads(list);
      if (autoCreate && list.length === 0 && currentUser?.role === 'teacher') {
        try {
          const created = await api.post('/chat/threads', { type: 'teacher_admin' });
          setThreads([created.data]);
          setActiveId(created.data.id);
        } catch { /* ignore */ }
      }
    } catch {
      toast.error('Failed to load chat threads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThreads(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, refetchVer]);

  const refreshThreads = () => {
    refetchSignal.current += 1;
    setRefetchVer(v => v + 1);
  };

  // Load messages when active thread changes (call extracted helper to avoid lint rule)
  const loadMessages = async (tid) => {
    try {
      const res = await api.get(`/chat/threads/${tid}/messages`);
      setMessages(res.data || []);
      try { await api.post(`/chat/threads/${tid}/read`); } catch { /* silent */ }
    } catch {
      toast.error('Failed to load messages');
    }
  };

  useEffect(() => {
    if (activeId) loadMessages(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // WebSocket lifecycle
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem('token') || '';
    if (!token) {
      if (mounted) setWsState('closed');
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = base.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/api/ws/chat?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => { if (mounted) setWsState('open'); };
    ws.onclose = () => { if (mounted) setWsState('closed'); };
    ws.onerror = () => { if (mounted) setWsState('closed'); };
    ws.onmessage = (ev) => {
      if (!mounted) return;
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'message') {
          refetchSignal.current += 1;
          setRefetchVer(v => v + 1);
          if (data.thread_id === activeIdRef.current) {
            setMessages(prev => [...prev, data.message]);
            api.post(`/chat/threads/${data.thread_id}/read`).catch(() => {});
          }
        }
      } catch { /* ignore */ }
    };
    return () => {
      mounted = false;
      try { ws.close(); } catch { /* ignore */ }
    };
  }, []);

  const handleSend = async () => {
    if (!composer.trim() || !activeId) return;
    const body = composer;
    setComposer('');
    try {
      const res = await api.post(`/chat/threads/${activeId}/messages`, { body });
      setMessages(prev => [...prev, res.data]);
      refreshThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
      setComposer(body);
    }
  };

  const startNewThread = async () => {
    try {
      const res = await api.post('/chat/threads', { type: 'teacher_admin' });
      setActiveId(res.data.id);
      refreshThreads();
    } catch (e) { toast.error('Failed to create thread'); }
  };

  const activeThread = threads.find(t => t.id === activeId);

  return (
    <div className="flex flex-col h-[600px] nb-card overflow-hidden" data-testid="chat-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-200 bg-slate-50">
        <h2 className="font-bold text-slate-900 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-blue-600" /> Messages
        </h2>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${wsState === 'open' ? 'bg-emerald-100 text-emerald-700' : wsState === 'connecting' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`} data-testid="chat-ws-state">
            ● {wsState === 'open' ? 'Live' : wsState === 'connecting' ? 'Connecting' : 'Polling'}
          </span>
          {currentUser?.role === 'teacher' && (
            <Button size="sm" variant="outline" onClick={startNewThread} data-testid="new-chat-btn">
              <Plus className="w-3 h-3 mr-1" /> New
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        <div className="w-64 border-r-2 border-slate-200 overflow-y-auto flex-shrink-0">
          {loading ? (
            <div className="p-4 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>
          ) : threads.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">No threads yet</div>
          ) : threads.map(t => (
            <button key={t.id} onClick={() => setActiveId(t.id)}
              className={`w-full text-left px-3 py-3 border-b border-slate-100 hover:bg-slate-50 ${activeId === t.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''}`}
              data-testid={`thread-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-sm text-slate-900 truncate">{t.title}</span>
                {t.unread_count > 0 && <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">{t.unread_count}</span>}
              </div>
              <p className="text-xs text-slate-500 truncate mt-0.5">{t.last_message_preview || 'No messages yet'}</p>
            </button>
          ))}
        </div>
        <div className="flex-1 flex flex-col min-w-0">
          {activeThread ? (
            <>
              <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
                <h3 className="font-bold text-sm text-slate-900">{activeThread.title}</h3>
                <p className="text-[10px] text-slate-500">{activeThread.participants?.length || 0} participants</p>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="chat-messages">
                {messages.length === 0 ? (
                  <p className="text-center text-slate-400 text-sm py-8">No messages yet — say hello!</p>
                ) : messages.map(m => {
                  const mine = m.sender_id === currentUser?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${m.id}`}>
                      <div className={`max-w-[70%] rounded-2xl px-3 py-2 ${mine ? 'bg-blue-600 text-white' : 'bg-white border-2 border-slate-200 text-slate-900'}`}>
                        {!mine && <p className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_name} • {m.sender_role}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[10px] mt-0.5 ${mine ? 'text-blue-100' : 'text-slate-400'}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t-2 border-slate-200 p-3 flex items-center gap-2 bg-white">
                <Input value={composer} onChange={e => setComposer(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder="Type a message…" className="flex-1" data-testid="chat-composer" />
                <Button onClick={handleSend} disabled={!composer.trim()} className="bg-blue-600 hover:bg-blue-700 font-bold" data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a thread to start chatting</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
